<template>
  <v-container fluid class="pa-0">
    <v-card>
      <v-card-title>{{this.$t('payment.application.certificate')}}</v-card-title>
      <v-card-text>
        <v-container class="pa-0">
          <HeaderSection :title="localTitle" @editTitle="editTitle" />
          <DetailRows :rows="details" @editRow="editRow" />
          <SummaryInformationTable
            :headers="sumTableHeaders"
            :items="summaryInformationItems"
          />
          <SectionTitle :title="$t('payment.certificate.executedAgainst')" />
          <DataTableSection
            :headers="headers"
            :items="valueOfWorks"
            :summaryText="$t('payment.certificate.valueOfWorkExecuted')"
          />
          <DataTableSection
            :headers="headers"
            :items="variations"
            :summaryText="$t('payment.certificate.valueOfVariation')"
          />
          <DataTableSection
            :headers="headers"
            :items="claims"
            :summaryText="$t('payment.certificate.valueOfClaims')"
          />
          <DataTableSection
            :headers="headers"
            :items="amendments"
            :summaryText="$t('payment.certificate.valueOfAmendments')"
          />
          <SummaryTable
            :headers="headers"
            :items="contractTotal"
            :no-header="true"
          />
          <EditableDataTableSection
            :fixedRows="true"
            :headers="headers"
            :items="deductionItems"
            :summaryText="$t('payment.certificate.sumOfDeductions')"
            @editRow="editRow"
          />
          <SummaryTable
            :headers="headers"
            :items="totalAfterDeductions"
            :no-header="true"
          />
          <CertificationTables
            :headers="headers"
            :previouslyCertifiedTotal="previouslyCertifiedTotal"
            :currentCertificationTotal="currentCertificationTotal"
          />
          <SectionTitle
            :title="$t('payment.certificate.employerRepresentatives')"
          />
        </v-container>
      </v-card-text>
      <v-btn class="mb-4 mx-4" color="primary" @click="handleClickGeneratePdf">{{ this.$t('payment.certificate.generatePDF') }}</v-btn>
    </v-card>
  </v-container>
</template>

<script>
import {
  DEDUCTION_TYPE,
  DETAILS_FIELD_TYPE,
  initializeDeductions,
  initializeHeaders,
  initializeProjectDetailsData,
  initializeSumTableHeaders,
  createSummaryInformationItems,
  initializeValueOfWorks,
  initializeVariations,
  initializeAmendments,
  initializeClaims,
  PAYMENT_CERTIFICATE_ROW_TYPE
} from '@/helpers'
import {
  createPaymentCertificatePdf,
  createPaymentCertificateRow,
  deletePaymentCertificateRow,
  getPaymentCertificate,
  updatePaymentCertificate,
  updatePaymentCertificateRow,
  getVariationsForContract
} from '@/services'

export default {
  props: {
    title: String,
    payment: Object,
    contract: Object,
    currencies: Array,
    paymentMilestones: Array,
    paymentCertificate: Object,
    paymentApplications: Array
  },
  data () {
    return {
      adjustment: [],
      contractTotal: [],
      currentCertificationTotal: [],
      deductions: [],
      headers: [],
      localTitle: this.title || '',
      options: [],
      PAYMENT_CERTIFICATE_ROW_TYPE,
      previouslyCertifiedTotal: [],
      projectDetailsData: initializeProjectDetailsData(
        this.contract,
        this.payment,
        this.paymentCertificate
      ),
      summaryInformationItems: [],
      sumTableHeaders: initializeSumTableHeaders(),
      totalAfterDeductions: [],
      paymentCertificateData: this.paymentCertificate,
      claims: [],
      variations: [],
      amendments: [],
      valueOfWorks: []
    }
  },
  computed: {
    contractId () {
      return this.$route.params.contractId
    },
    certificateId () {
      return this.paymentCertificateData.id
    },
    details () {
      return this.splitIntoRows(this.projectDetailsData, 4)
    },
    deductionItems () {
      return initializeDeductions(this.paymentCertificateData)
    },
    contractCurrency () {
      return this.currencies[0].key
    }
  },
  watch: {
    deductionItems: {
      handler () {
        this.calculateTotalAfterDeductions()
      },
      deep: true
    },
    totalAfterDeductions: {
      handler () {
        this.calculateSummary()
        this.calculatePreviouslyCertifiedTotal()
        this.calculateCurrentCertificationTotal()
      },
      deep: true
    }
  },
  async mounted () {
    await this.handleFetchData()

    const claims = await getVariationsForContract(this.contractId, 'claim')
    const amendments = await getVariationsForContract(this.contractId, 'amendment')

    this.claims = initializeClaims(this.payment, this.paymentApplications, this.contract, this.paymentMilestones, claims)
    this.variations = initializeVariations(this.payment, this.paymentApplications, this.contract, this.paymentMilestones, claims, amendments)
    this.amendments = initializeAmendments(this.payment, this.paymentApplications, this.contract, this.paymentMilestones, amendments)
    this.valueOfWorks = initializeValueOfWorks(this.payment, this.paymentApplications, this.contract, this.paymentMilestones, amendments)
    this.headers = initializeHeaders(this.contractCurrency)

    this.calculateSummary()
    this.calculateTotals()
    this.calculateTotalAfterDeductions()
    this.calculatePreviouslyCertifiedTotal()
    this.calculateCurrentCertificationTotal()
  },
  methods: {
    calculateSummary () {
      const contractAmount = this.contract.signedPriceCents
      const claims = this.claims.reduce((acc, item) => {
        return acc + (parseFloat(item.totalCents) || 0)
      }, 0)
      const variations = this.variations.reduce((acc, item) => {
        return acc + (parseFloat(item.totalCents) || 0)
      }, 0)
      const amendments = this.amendments.reduce((acc, item) => {
        return acc + (parseFloat(item.totalCents) || 0)
      }, 0)
      const contractPrice = contractAmount + claims + variations + amendments

      this.summaryInformationItems = createSummaryInformationItems({
        contractAmount,
        amendments,
        claims,
        variations,
        contractPrice,
        contractCurrency: this.contractCurrency
      })
    },
    splitIntoRows (data, chunkSize) {
      const rows = []
      for (let i = 0; i < data.length; i += chunkSize) {
        rows.push({
          id: i,
          items: data.slice(i, i + chunkSize)
        })
      }
      return rows
    },
    calculateTotals () {
      const totals = {
        ref: '',
        description: this.$t('payment.certificate.totalContractPrice'),
        totalCents: 0,
        cumulativeCents: 0,
        cumulativePreviousCents: 0,
        paymentCents: 0
      }

      const arrays = [
        this.valueOfWorks,
        this.claims,
        this.variations,
        this.amendments
      ]

      arrays.forEach((array) => {
        array.forEach((item) => {
          totals.totalCents += parseFloat(item.totalCents) || 0
          totals.cumulativeCents += parseFloat(item.cumulativeCents) || 0
          totals.cumulativePreviousCents +=
            parseFloat(item.cumulativePreviousCents) || 0
          totals.paymentCents += parseFloat(item.paymentCents) || 0
        })
      })

      this.contractTotal = [totals]
    },
    calculateTotalAfterDeductions () {
      const totals = {
        ref: '',
        description: this.$t('payment.certificate.totalAfterDeductions'),
        totalCents: 0,
        cumulativeCents: 0,
        cumulativePreviousCents: 0,
        paymentCents: 0
      }

      const arrays = [
        this.valueOfWorks,
        this.claims,
        this.variations,
        this.amendments,
        this.deductionItems
      ]

      arrays.forEach((array) => {
        array.forEach((item) => {
          totals.totalCents += parseFloat(item.totalCents) || 0
          totals.cumulativeCents += parseFloat(item.cumulativeCents) || 0
          totals.cumulativePreviousCents +=
            parseFloat(item.cumulativePreviousCents) || 0
          totals.paymentCents += parseFloat(item.paymentCents) || 0
        })
      })

      this.totalAfterDeductions = [totals]
    },
    calculatePreviouslyCertifiedTotal () {
      const totals = {
        ref: '',
        description: this.$t('payment.certificate.prevCertifiedAmount'),
        paymentCents: this.totalAfterDeductions[0].cumulativePreviousCents
      }
      this.previouslyCertifiedTotal = [totals]
    },
    calculateCurrentCertificationTotal () {
      const totals = {
        ref: '',
        description: this.$t('payment.certificate.currentCertifiedAmount'),
        paymentCents: this.totalAfterDeductions[0].paymentCents
      }
      this.currentCertificationTotal = [totals]
    },
    async addRow (item) {
      await createPaymentCertificateRow(
        this.contractId,
        this.certificateId,
        item
      )
      await this.handleFetchData()
    },
    async removeRow (item) {
      await deletePaymentCertificateRow(
        this.contractId,
        this.certificateId,
        item.id,
        item
      )
      await this.handleFetchData()
    },
    async editTitle (value) {
      const item = {
        ...this.paymentCertificateData,
        title: value
      }

      this.localTitle = value

      delete item.type
      delete item.paymentCertificateRows

      await updatePaymentCertificate(
        this.contractId,
        this.certificateId,
        item
      )
    },
    async editRow (item) {
      if (Object.values(DETAILS_FIELD_TYPE).includes(item.key)) {
        const editedItem = {
          ...this.paymentCertificateData,
          [item.key]: item.content
        }
        delete editedItem.type
        delete editedItem.paymentCertificateRows

        await updatePaymentCertificate(
          this.contractId,
          this.certificateId,
          editedItem
        )
      } else {
        if (Object.values(DEDUCTION_TYPE).includes(item.type)) {
          const editedItem = {
            ...this.paymentCertificateData,
            ...item
          }
          delete editedItem.type
          delete editedItem.paymentCertificateRows

          await updatePaymentCertificate(
            this.contractId,
            this.certificateId,
            editedItem
          )
        } else {
          await updatePaymentCertificateRow(
            this.contractId,
            this.certificateId,
            item.id,
            item
          )
        }
      }

      await this.handleFetchData()
    },
    handleClickGeneratePdf () {
      const data = {
        paymentCertificate: this.paymentCertificateData,
        details: this.details,
        summary: this.summaryInformationItems,
        valueOfWorks: this.valueOfWorks,
        variations: this.variations,
        claims: this.claims,
        amendments: this.amendments,
        contractTotal: this.contractTotal,
        deductions: this.deductionItems,
        totalAfterDeductions: this.totalAfterDeductions,
        previouslyCertifiedTotal: this.previouslyCertifiedTotal,
        currentCertificationTotal: this.currentCertificationTotal,
        title: this.localTitle,
        payment: this.payment,
        contract: this.contract,
        paymentMilestones: this.paymentMilestones,
        contractCurrency: this.contractCurrency
      }

      createPaymentCertificatePdf(data)
    },
    async handleFetchData () {
      const paymentCertificate = await getPaymentCertificate(this.contractId)
      this.paymentCertificateData = paymentCertificate
    }
  }
}
</script>

<style>

.border {
  border: 1px solid #000;
  border-radius: 0;
}
.header-text {
  color: black;
  font-weight: 500;
  text-align: center;
  display: flex;
  align-items: center;
}
.column-width {
  min-width: 250px;
}
.custom-text-width {
  width: 150px;
}
.no-wrap-row {
  flex-wrap: nowrap;
}
</style>
